import { ErrorMessage, Flex, Grid, NumberField } from "@simplybusiness/mobius";
import type { QuestionComponentProps } from "../..";
import {
  useCallback,
  useRef,
  useState,
  Fragment,
  useMemo,
  useId,
  type JSX,
} from "react";
import {
  clampForLabel,
  computeTotal,
  buildInitialPercentages,
  type NormalizedLabel,
} from "./percentageQuestionUtils";
import { useAnswers } from "../../../../../../../features/questionnaire";

export const PercentageQuestion = ({
  questionFieldProps,
  config,
}: QuestionComponentProps): JSX.Element => {
  const answers = useAnswers();
  const {
    value,
    validation,
    onChange,
    // we deliberately extract onBlur so it isn't passed to each field
    onBlur,
    questionLabelProps,
    helpInfo,
    errorMessage,
    name,
    id,
    ...otherProps
  } = questionFieldProps;

  const { labels } = config || {};

  // Normalize labels once - separate display from data storage keys
  const normalizedLabels = useMemo(
    (): NormalizedLabel[] =>
      (labels as string[] | undefined)?.map(label => ({
        display: label, // Original case for UI display
        key: label.toLowerCase(), // Lowercase for data storage
      })) ?? [],
    [labels],
  );

  // Build initial state from individual answer entries using normalized labels
  const [percentages, setPercentages] = useState<Record<string, number>>(() =>
    buildInitialPercentages(normalizedLabels, answers, name),
  );

  const total = computeTotal(percentages);

  const errorMessageId = useId();

  const containerRef = useRef<HTMLDivElement | null>(null);

  // Fire onBlur only when focus leaves the entire component
  const handleContainerBlur = useCallback(
    (e: React.FocusEvent) => {
      const nextFocus = e.relatedTarget as Node | null;
      if (
        containerRef.current &&
        nextFocus &&
        containerRef.current.contains(nextFocus)
      ) {
        // Focus is still within the component; ignore
        return;
      }
      if (onBlur) {
        // Create both parent answer and individual bracketed answers
        const parentValue = Object.values(percentages).join(",");

        const individualAnswers = Object.fromEntries(
          Object.entries(percentages).map(([label, value]) => [
            `${questionFieldProps.name}[${label}]`,
            String(value),
          ]),
        );

        // Send combined object with both parent and individual answers
        onBlur({
          target: {
            name: questionFieldProps.name,
            value: {
              [questionFieldProps.name]: parentValue,
              ...individualAnswers,
            },
          },
        });
      }
    },
    [onBlur, percentages, questionFieldProps.name],
  );

  return (
    <div onBlur={handleContainerBlur} ref={containerRef} tabIndex={-1}>
      <Flex flexDirection="column" gap="sm">
        <Grid columns={2} alignItems="center" rowGap="sm">
          {normalizedLabels.map(({ display, key }) => {
            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const newPercentages = { ...percentages };
              newPercentages[key] = Number(e.target.value);
              setPercentages(newPercentages);
            };
            return (
              <Fragment key={`${id}-${display}`}>
                <label
                  id={`${id}-${display}-label`}
                  htmlFor={`${id}-${display}`}
                >
                  {display}
                </label>
                <NumberField
                  {...otherProps}
                  step={1}
                  id={`${id}-${display}`}
                  aria-labelledby={`${id}-${display}-label`}
                  aria-errormessage={errorMessage ? errorMessageId : undefined}
                  aria-invalid={!!errorMessage}
                  onChange={handleChange}
                  value={String(percentages[key] || 0)}
                  // Do NOT pass onBlur so validation waits for container blur
                  minValue={0}
                  maxValue={clampForLabel(percentages, key, 100)}
                  suffixInside={<span>%</span>}
                />
              </Fragment>
            );
          })}
          <label>Total</label>
          <span className="mobius-percentage-question__total">{total}%</span>
        </Grid>
        {!!errorMessage && (
          <ErrorMessage id={errorMessageId} errorMessage={errorMessage} />
        )}
      </Flex>
    </div>
  );
};
